import { type ClassValue, clsx } from "clsx";
import dayjs from "dayjs";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

type TimeUnit = "second" | "millisecond" | "microsecond" | "nanosecond";

export function formatTimestamp(
	timestamp: number,
	format = "YYYY-MM-DD HH:mm",
	unit: TimeUnit = "second",
): string {
	// 根据单位转换为毫秒
	const milliseconds = (() => {
		switch (unit) {
			case "second":
				return timestamp * 1000;
			case "millisecond":
				return timestamp;
			case "microsecond":
				return timestamp / 1000;
			case "nanosecond":
				return timestamp / 1000000;
			default:
				return timestamp * 1000;
		}
	})();

	return dayjs(milliseconds).format(format);
}

// 自动判断时间单位的格式化函数
export function formatTimestampAuto(
	timestamp: number,
	format = "YYYY-MM-DD HH:mm",
): string {
	// 根据数值大小自动判断单位
	const unit = (() => {
		const now = Date.now();
		const diff = Math.abs(timestamp - now);

		if (diff > 1000000000000) {
			// 大于 1000000000000 纳秒
			return "nanosecond";
		}
		if (diff > 1000000000) {
			// 大于 1000000000 微秒
			return "microsecond";
		}
		if (diff > 1000000) {
			// 大于 1000000 毫秒
			return "millisecond";
		}
		return "second";
	})();

	return formatTimestamp(timestamp, format, unit);
}

export async function sleep(delay = 1000) {
	return new Promise((resolve) => {
		setTimeout(resolve, delay);
	});
}

export function fromNow(timestamp: string | number) {
	const date = new Date(Number(timestamp) * 1000);
	const now = new Date();
	const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

	// 定义时间单位
	const minute = 60;
	const hour = minute * 60;
	const day = hour * 24;
	const week = day * 7;
	const month = day * 30;
	const year = day * 365;

	// 根据时间差返回对应的描述
	if (diffInSeconds < minute) {
		return "just now";
	}

	if (diffInSeconds < hour) {
		const minutes = Math.floor(diffInSeconds / minute);
		return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
	}

	if (diffInSeconds < day) {
		const hours = Math.floor(diffInSeconds / hour);
		return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
	}

	if (diffInSeconds < week) {
		const days = Math.floor(diffInSeconds / day);
		return `${days} ${days === 1 ? "day" : "days"} ago`;
	}

	if (diffInSeconds < month) {
		const weeks = Math.floor(diffInSeconds / week);
		return `${weeks} ${weeks === 1 ? "week" : "weeks"} ago`;
	}

	if (diffInSeconds < year) {
		const months = Math.floor(diffInSeconds / month);
		return `${months} ${months === 1 ? "month" : "months"} ago`;
	}

	const years = Math.floor(diffInSeconds / year);
	return `${years} ${years === 1 ? "year" : "years"} ago`;
}

export function toBase64(url: string): Promise<string> {
	return new Promise((resolve, reject) => {
		const img = new window.Image();
		img.crossOrigin = "Anonymous";
		img.onload = () => {
			const canvas = document.createElement("canvas");
			canvas.width = img.width;
			canvas.height = img.height;
			const ctx = canvas.getContext("2d");
			ctx?.drawImage(img, 0, 0);
			resolve(canvas.toDataURL("image/png"));
		};
		img.onerror = reject;
		img.src = url;
	});
}

/**
 * 将数字格式化为带 K, M, B 单位的热度值。
 * - 小于50的数字，显示原始整数。
 * - 大于等于1000的数字，会转换为带两位小数的 K, M, B 格式。
 * - 50到999之间的数字，显示为整数。
 *
 * @param {number | string | null} num - 需要格式化的原始数字。
 * @returns {string} 格式化后的字符串。
 */
export function formatPopularity(num: number | string | null) {
	// First, we need to ensure the input is a valid number.
	// 首先，我们需要确保输入是一个有效的数字。
	const number = Number.parseFloat(String(num));

	// Handle invalid or negative inputs.
	// 处理无效输入或负数。
	if (Number.isNaN(number) || number < 0) {
		return "0";
	}

	// Rule 1: If the number is less than 50, show the actual value (as an integer).
	// 规则1：如果数字小于50，显示实际值（整数）。
	if (number < 50) {
		return String(Math.floor(number));
	}

	// Rule 2: Format for Billions (B).
	// 规则2：处理"十亿"（B）单位。
	if (number >= 1_000_000_000) {
		return `${(number / 1_000_000_000).toFixed(2)}B`;
	}

	// Rule 3: Format for Millions (M).
	// 规则3：处理"百万"（M）单位。
	if (number >= 1_000_000) {
		return `${(number / 1_000_000).toFixed(2)}M`;
	}

	// Rule 4: Format for Thousands (K).
	// 规则4：处理"千"（K）单位。
	if (number >= 1000) {
		return `${(number / 1000).toFixed(2)}K`;
	}

	// For numbers between 50 and 999, show the integer value.
	// 对于50到999之间的数字，显示整数值。
	return String(Math.floor(number));
}

/**
 * Format a number to thousands format
 * @param {number | string} num - the number to be formatted
 * @param {number | null} decimals - the number of decimal places, default is null
 * @returns {string} the formatted thousands string
 *
 * @example
 * formatThousands(1234567) // "1,234,567"
 * formatThousands(1234567.89, 2) // "1,234,567.89"
 * formatThousands(1234.5678, 2) // "1,234.57"
 * formatThousands(1234.5678) // "1,234.5678"
 */
export function formatThousands(
	num: number | string,
	decimals: number | null = null,
): string {
	// 转换为数字
	const number = Number(num);

	// 处理无效输入
	if (Number.isNaN(number)) {
		return "0";
	}

	if (decimals === null) {
		// 不做四舍五入，原样保留小数位，仅做千分位分隔
		const [intPart, decPart] = String(num).split(".");
		const intStr = Number(intPart).toLocaleString("en-US");
		return decPart !== undefined ? `${intStr}.${decPart}` : intStr;
	}
	// 保留对应小数位并四舍五入
	return number.toLocaleString("en-US", {
		minimumFractionDigits: decimals,
		maximumFractionDigits: decimals,
	});
}
export function forTagBgColor(tagName: string) {
	const colorIndex = tagName.charCodeAt(0) % 3;
	const bgColors = ["bg-c1", "bg-c2", "bg-bg3"];
	return bgColors[colorIndex];
}

export async function copyText(text: string) {
	try {
		await navigator.clipboard.writeText(text);
		return true;
	} catch (error) {
		const textArea = document.createElement("textarea");
		textArea.value = text;
		textArea.style.top = "-9999px";
		textArea.style.left = "-9999px";
		textArea.style.position = "fixed";
		document.body.appendChild(textArea);
		textArea.focus();
		textArea.select();
		const successful = document.execCommand("copy");
		document.body.removeChild(textArea);
		if (successful) {
			return true;
		}
		toast.error(`Copy Failed: ${error}`);
		throw error;
	}
}
